import React, { useMemo, useState } from "react";
import { ChevronDown, RotateCcw } from "lucide-react";
import {
  accessContextsForWaterbody,
  accessLabelKey,
  habitatGroupsForWaterbody,
  habitatLabelKey,
  normalizeFishingSetting,
} from "../../features/fishingIntelligence/fishingSetting";
import "../../styles/components/SpeciesFishingSetting.css";
import { compatibleAccess, compatibleFeature } from "../../features/fishingIntelligence/marineFishingContext";

const summaryFor = (setting, t) => {
  const labels = [];
  if (setting.accessContext) {
    labels.push(t(accessLabelKey(setting.accessContext)));
  }
  setting.habitatFeatures.forEach((feature) => {
    labels.push(t(habitatLabelKey(feature)));
  });
  return labels.length > 0
    ? labels.join(" · ")
    : t("fishing_today_setting_not_specified");
};

const SettingButton = ({ label, onClick, selected }) => (
  <button
    type="button"
    className="rp-fishing-setting__chip"
    aria-pressed={selected}
    onClick={onClick}
  >
    {label}
  </button>
);

export default function SpeciesFishingSetting({
  declaredHabitat,
  footer = null,
  idPrefix = "fishing-setting",
  isLoading,
  onChange,
  t,
  value,
  waterbody,
  platform = null,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const localSetting = normalizeFishingSetting(value);
  const confirmedSetting =
    declaredHabitat?.source === "user_confirmed" && !isLoading
      ? normalizeFishingSetting({
          habitatFeatures: declaredHabitat.habitatFeatures,
          accessContext: declaredHabitat.accessContext,
        })
      : localSetting;
  const accessOptions = useMemo(
    () => accessContextsForWaterbody(waterbody).filter((item) => compatibleAccess(platform, item)),
    [waterbody, platform]
  );
  const habitatGroups = useMemo(
    () => habitatGroupsForWaterbody(waterbody),
    [waterbody]
  );
  const hasSelection =
    localSetting.accessContext !== null || localSetting.habitatFeatures.length > 0;
  const incompatible = !compatibleAccess(platform, localSetting.accessContext)
    || localSetting.habitatFeatures.some((item) => !compatibleFeature(platform, item));

  const updateAccess = (accessContext) => {
    onChange({
      ...localSetting,
      accessContext:
        localSetting.accessContext === accessContext ? null : accessContext,
    });
  };
  const updateFeature = (feature) => {
    const selected = localSetting.habitatFeatures.includes(feature);
    onChange({
      ...localSetting,
      habitatFeatures: selected
        ? localSetting.habitatFeatures.filter((item) => item !== feature)
        : [...localSetting.habitatFeatures, feature],
    });
  };

  return (
    <section
      className="rp-fishing-setting"
      aria-labelledby={`${idPrefix}-title`}
      aria-busy={isLoading}
    >
      <div className="rp-fishing-setting__summary-row">
        <div className="rp-fishing-setting__summary-copy">
          <div className="rp-fishing-setting__title-line">
            <h4 id={`${idPrefix}-title`}>{t("fishing_today_setting_title")}</h4>
            <span>{t("fishing_today_setting_optional")}</span>
          </div>
          <p aria-live="polite">{summaryFor(confirmedSetting, t)}</p>
        </div>
        <button
          type="button"
          className="rp-fishing-setting__toggle"
          aria-expanded={isExpanded}
          aria-controls={`${idPrefix}-controls`}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {t(
            hasSelection
              ? "fishing_today_setting_edit"
              : "fishing_today_setting_add"
          )}
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={isExpanded ? "is-expanded" : ""}
          />
        </button>
      </div>

      {isExpanded ? (
        <div id={`${idPrefix}-controls`} className="rp-fishing-setting__controls">
          <p className="rp-fishing-setting__helper">
            {incompatible ? <span role="status">{t("marine_context_incompatible")}</span> : null}
            {t("fishing_today_setting_helper")}
            {declaredHabitat?.scoringActive === true ? null : (
              <span>{t("fishing_today_setting_non_scoring")}</span>
            )}
          </p>

          <fieldset>
            <legend>{t("fishing_today_setting_access_question")}</legend>
            <div className="rp-fishing-setting__chips">
              {accessOptions.map((accessContext) => (
                <SettingButton
                  key={accessContext}
                  label={t(accessLabelKey(accessContext))}
                  selected={localSetting.accessContext === accessContext}
                  onClick={() => updateAccess(accessContext)}
                />
              ))}
            </div>
            {localSetting.accessContext ? (
              <button
                type="button"
                className="rp-fishing-setting__clear"
                onClick={() => onChange({ ...localSetting, accessContext: null })}
              >
                {t("fishing_today_setting_clear_access")}
              </button>
            ) : null}
          </fieldset>

          <fieldset>
            <legend>{t("fishing_today_setting_habitat_question")}</legend>
            <span className="rp-fishing-setting__field-note">
              {t("fishing_today_setting_choose_all")}
            </span>
            <div className="rp-fishing-setting__chips">
              {habitatGroups.common.filter((feature) => compatibleFeature(platform, feature)).map((feature) => (
                <SettingButton
                  key={feature}
                  label={t(habitatLabelKey(feature))}
                  selected={localSetting.habitatFeatures.includes(feature)}
                  onClick={() => updateFeature(feature)}
                />
              ))}
            </div>
            <button
              type="button"
              className="rp-fishing-setting__more"
              aria-expanded={showAdvanced}
              onClick={() => setShowAdvanced((shown) => !shown)}
            >
              {t(
                showAdvanced
                  ? "fishing_today_setting_hide_more"
                  : "fishing_today_setting_show_more"
              )}
              <ChevronDown
                size={15}
                aria-hidden="true"
                className={showAdvanced ? "is-expanded" : ""}
              />
            </button>
            {showAdvanced ? (
              <div className="rp-fishing-setting__chips rp-fishing-setting__chips--advanced">
                {habitatGroups.advanced.filter((feature) => compatibleFeature(platform, feature)).map((feature) => (
                  <SettingButton
                    key={feature}
                    label={t(habitatLabelKey(feature))}
                    selected={localSetting.habitatFeatures.includes(feature)}
                    onClick={() => updateFeature(feature)}
                  />
                ))}
              </div>
            ) : null}
          </fieldset>

          {hasSelection ? (
            <button
              type="button"
              className="rp-fishing-setting__reset"
              onClick={() => onChange({ habitatFeatures: [], accessContext: null })}
            >
              <RotateCcw size={15} aria-hidden="true" />
              {t("fishing_today_setting_clear_all")}
            </button>
          ) : null}
          {footer}
        </div>
      ) : null}
    </section>
  );
}
